import { useEffect, useRef, useState, type ReactNode } from "react";
import { enqueueSnackbar } from "notistack";
import { PwaInstallContext, type PwaInstallPromptEvent } from "./PwaInstallContext";

// --------------------------------- TYPES ---------------------------------
type PwaInstallProviderProps = {
	readonly children: ReactNode;
};

// ------------------------------- COMPONENT -------------------------------
export default function PwaInstallProvider({ children }: Readonly<PwaInstallProviderProps>) {
	// -------------------------------- STATES --------------------------------
	const [evento, setEvento] = useState<PwaInstallPromptEvent | null>(null);
	const [standalone, setStandalone] = useState(false);
	const [instalado, setInstalado] = useState(false);
	const [instalando, setInstalando] = useState(false);
	const envioEmAndamento = useRef(false);
	const montado = useRef(false);

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		montado.current = true;
		const modoStandalone = globalThis.matchMedia("(display-mode: standalone)");
		setStandalone(modoStandalone.matches);

		const capturarInstalacao = (event: PwaInstallPromptEvent) => {
			if (modoStandalone.matches) return;
			event.preventDefault();
			setEvento(event);
		};
		const concluirInstalacao = () => {
			setInstalado(true);
			setEvento(null);
		};
		const atualizarModo = () => {
			setStandalone(modoStandalone.matches);
			if (modoStandalone.matches) setEvento(null);
		};

		globalThis.addEventListener("beforeinstallprompt", capturarInstalacao);
		globalThis.addEventListener("appinstalled", concluirInstalacao);
		modoStandalone.addEventListener("change", atualizarModo);
		return () => {
			montado.current = false;
			globalThis.removeEventListener("beforeinstallprompt", capturarInstalacao);
			globalThis.removeEventListener("appinstalled", concluirInstalacao);
			modoStandalone.removeEventListener("change", atualizarModo);
		};
	}, []);

	// ---------------------------- DERIVED STATE -----------------------------
	const podeInstalar = evento !== null && !standalone && !instalado;

	// ------------------------------- HANDLERS -------------------------------
	const instalar = async () => {
		if (!evento || !podeInstalar || envioEmAndamento.current) return;
		envioEmAndamento.current = true;
		setInstalando(true);
		try {
			await evento.prompt();
			const escolha = await evento.userChoice;
			if (montado.current && escolha.outcome === "accepted") setInstalado(true);
		} catch {
			if (montado.current)
				enqueueSnackbar("Não foi possível abrir a instalação do aplicativo. Tente pelo menu do navegador.", {
					variant: "error",
				});
		} finally {
			envioEmAndamento.current = false;
			if (montado.current) {
				setEvento((atual) => (atual === evento ? null : atual));
				setInstalando(false);
			}
		}
	};

	// -------------------------------- RETURN --------------------------------
	return <PwaInstallContext value={{ podeInstalar, instalando, aoInstalar: instalar }}>{children}</PwaInstallContext>;
}
